import React, { useState } from "react";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import CardServicesAdvisers from "frontend/Components/DesignSystem/Cards/CardServicesAdvisers";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import ButtonGreen from "frontend/Components/DesignSystem/Buttons/ButtonGreen";
import { useNavigate } from "react-router-dom";
import "../../../css/Adviser/Pages/AdviserHomePage.css";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as RosemountInMediaIcon } from "../../../assets/icons/Rosemount in Media.svg";
import { ReactComponent as HearFromOurAdvisersIcon } from "../../../assets/icons/hear from our advisers.svg";
import { ReactComponent as EventsIcon } from "../../../assets/icons/Events.svg";
import FinancialPerformanceDetails from "frontend/Components/FinancialPerformanceDocuments/FinancialPerformanceDetails";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import Testimonials from "../../Components/Testimonials/Testimonials";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import VideoCarouselPopup from "../../Components/DesignSystem/VideoCarousel";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";

/**
 * AdviserHomePage component renders the main landing page for advisers.
 *
 * This page provides navigation, hero section, services, media, events,
 * financial performance details, FAQs, testimonials, and contact information
 * tailored for financial advisers. It includes interactive elements such as
 * modal popups and navigation buttons.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered AdviserHomePage component.
 *
 * @example
 * // Usage in a React Router route
 * <Route path="/Adviser" element={<AdviserHomePage />} />
 */
const AdviserHomePage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];

  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Toggles the state of the modal between open and closed.
   * Updates the `isModalOpen` state to its opposite value.
   *
   * @function
   * @returns {void}
   */
  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

 
  return (
    <div className="Adviser-Home-Page">
      <SeoHelmet
        page={SEO_PAGES.adviserHome}
        service={{
          name: "Adviser Network Support",
          description:
            "Rosemount provides compliance, training, technology and business growth support for mortgage advisers, protection specialists and financial planners.",
          serviceType: [
            "Mortgage Adviser Support",
            "Protection Adviser Support",
            "Financial Planning Adviser Support",
            "Compliance Support",
            "Business Growth Support",
            "Technology Support",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Advisers",
            url: getPageUrl("/Adviser"),
          },
        ]}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["advisers-home"]}
        altText="Two advisers sat by a laptop, one typing with the other instructing."
        isAdviser={true}
        children={
          <>
            <h1>Rosemount Financial Solutions (IFA) Ltd</h1>
            <div className="ButtonGreen">
              <ButtonGreen
                buttonText="Customers Page"
                onClick={() => navigate("/Customer")}
              />
            </div>
          </>
        }
      />
      <div className="Adviser-Services-Container">
        <h3>
          Are you an experienced adviser looking to grow your own business?
        </h3>
        <div className="Adviser-Services-Holder">
          <CardServicesAdvisers
            imgSrc={ImageDirectory["personal-approach-small"]}
            altText="An adviser sat down in a suit in a blue room."
            title="Personal Approach"
            description="Understanding your vision, fueling your success"
            link="/Adviser/PersonalApproach"
          />
          <CardServicesAdvisers
            imgSrc={ImageDirectory["support-small"]}
            altText="Two advisers speaking over paperwork and sharing a laugh."
            title="Support"
            description="Family values, professional strength"
            link="/Adviser/Support"
          />
          <CardServicesAdvisers
            imgSrc={ImageDirectory["technology-small"]}
            title="Technology"
            altText="An adviser working behind a laptop, typing."
            description="Driving growth through technology"
            link="/Adviser/Technology"
          />
        </div>
        <div className="Adviser-Services-Container Adviser-Services-Holder">
          <CardProduct
            Icon={RosemountInMediaIcon}
            title="Rosemount in the Media"
            link="/RosemountInMedia"
            isAdviser={true}
          />
          <div onClick={handleToggleModal}>
            <CardProduct
              Icon={HearFromOurAdvisersIcon}
              title="Hear from our Advisers"
              isAdviser={true}
            />
          </div>
          <VideoCarouselPopup
            isOpen={isModalOpen}
            onClose={handleToggleModal}
            // videos={videos}
          />
          <CardProduct
            Icon={EventsIcon}
            title="Events"
            link="/Adviser/Events"
            isAdviser={true}
          />
        </div>
      </div>
      {/* <CardReviewAdviser /> */}
      <FinancialPerformanceDetails
        title="Mortgage and Financial Advisers"
        link="/Adviser/AboutUs"
        description="A truly independent, privately owned, full financial services network where you are not just a number"
      />
      <FAQContainer
        isAdviser={true}
        faqs={[
          {
            question: "Who are Rosemount and what makes your network unique?",
            answer:
              "Rosemount are a leading independent financial services network dedicated to providing comprehensive support for advisers. We pride ourselves on a personalised approach, bespoke business support, common-sense approach to compliance and a commitment to adviser growth.",
          },
          {
            question: "What is the size of the Rosemount network?",
            answer:
              "We are a continuously growing independent network with one of the best adviser to staff ratios in the country. We still remain a bespoke, independent, family owned network, dedicated to supporting you and your business.",
          },
          {
            question: "What types of advisers do you take on?",
            answer:
              "We are looking for ambitious advisers to join our network. We pride ourselves on our ability to support adviser and business growth. If you are considering changing network, moving into a new business area or wanting to start your own business, speak to us today. ",
          },
        ]}
      />
      <Testimonials isAdviser={true} />
      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
    </div>
  );
};

export default AdviserHomePage;
